import { useNavigate } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { selfBookedChannel } from "../../journal/appointment-history"
import { formatRange, minutesFromDayStart } from "../../journal/journal-time"
import { formatMoney, remainderMinor } from "../../journal/money"
import { statusVisual } from "../../journal/status-style"
import { formatDateLong } from "../../lib/format"
import { ProblemScreen } from "../../shell/states"
import { Badge } from "../../ui/badge"
import { Button } from "../../ui/button"
import { EmptyState } from "../../ui/empty-state"
import { MaskedField } from "../../ui/field"
import { SkeletonText } from "../../ui/skeleton"
import { formatPhone } from "./phone"
import { phoneAccessOf } from "./master-visibility"
import { noShowLabel, visitsLabel } from "./master-text"
import { MasterBackBar, MasterFrame, MasterNoEmployee } from "./master-frame"
import styles from "./master-visit-screen.module.css"
import { useMasterDay } from "./use-master-day"
import { useRevealPhone, useVisitActions } from "./use-master-actions"

/**
 * 5-М3 — карточка записи ГЛАЗАМИ МАСТЕРА.
 *
 * ЭТО НЕ УМЕНЬШЕННАЯ ПАНЕЛЬ 2-2.3.4. У администратора панель заканчивается рядом
 * «Отменить · Не пришёл»; у мастера отмены нет вовсе, и это не урезание, а другое
 * распределение ответственности: отмена меняет обязательства салона перед клиентом
 * и запускает уведомление, поэтому решение принимает салон (ТЗ §5.5). Вместо
 * отсутствующей кнопки стоит строка, объясняющая, к кому идти, — урезанный экран
 * не должен выглядеть сломанным.
 *
 * «НЕ ПРИШЁЛ» ОТДЕЛЁН И НЕ ПЕРВЫЙ В РЯДУ (проверка §5.16). Он стоит отдельной строкой
 * ниже пары «Клиент пришёл · Завершено» и нарисован как опасное действие: промах пальцем
 * по нему ставит человеку неявку, а это счётчик, предоплата и закрытая онлайн-запись.
 *
 * ТЕЛЕФОН И ДЕНЬГИ — ДВА СОСТОЯНИЯ КАЖДОЕ, и выключенное состояние это ОТСУТСТВИЕ
 * блока, а не серый прочерк (§5.2). Решение принимает сервер: он присылает phone = null
 * и priceMinor = null, а экран просто не рисует того, чего ему не дали.
 */

export interface MasterVisitSearch {
  readonly date?: string | undefined
  readonly branch?: string | undefined
}

export function MasterVisitScreen(props: {
  readonly appointmentId: string
  readonly search: MasterVisitSearch
}): ReactNode {
  const data = useMasterDay(props.search)
  const navigate = useNavigate()
  const [noShowComment, setNoShowComment] = useState("")
  const [noShowOpen, setNoShowOpen] = useState(false)

  const backToDay = useCallback(() => {
    void navigate({ to: "/my/day", search: { date: data.date } })
  }, [navigate, data.date])

  const actions = useVisitActions(backToDay)
  const reveal = useRevealPhone()

  const appointment =
    data.result?.appointments.find((item) => item.id === props.appointmentId) ?? null

  // САЛОН ВКЛЮЧИЛ «МАСТЕР ВИДИТ ТЕЛЕФОН» — номер открывается сам, при открытии карточки.
  // Не с лентой дня: там номер оказался бы в браузере для всех записей разом, хотя
  // мастер мог не посмотреть ни на один, и журнал открытий, который ТЗ требует вести
  // и в этом положении (04, Б12), либо молчал бы, либо лгал. Открытие карточки —
  // ровно одно открытие и ровно одна строка. Ref держит «уже открывали» для этой записи:
  // перерисовка карточки не должна писать в журнал второе открытие.
  const autoRevealed = useRef<string | null>(null)
  const autoRevealFor =
    appointment !== null && phoneAccessOf(appointment) === "on_open" ? appointment : null
  useEffect(() => {
    if (autoRevealFor === null || autoRevealed.current === autoRevealFor.id) return
    autoRevealed.current = autoRevealFor.id
    reveal.request(autoRevealFor.client.id, autoRevealFor.id)
  }, [autoRevealFor, reveal])

  if (data.employeeId === null) {
    return (
      <MasterFrame active="day">
        <MasterNoEmployee title="Запись" />
      </MasterFrame>
    )
  }

  if (data.problem !== null && data.result === null) {
    return (
      <MasterFrame active="day" stripe={data.availability.stripe} onStripeAction={data.reload}>
        <ProblemScreen problem={data.problem} onRetry={data.reload} />
      </MasterFrame>
    )
  }

  if (data.result === null) {
    return (
      <MasterFrame active="day">
        <MasterBackBar to="/my/day" label="Мой день" />
        <div className={styles.body}>
          <SkeletonText widths={[160, 220, 140]} height={14} />
        </div>
      </MasterFrame>
    )
  }

  if (appointment === null) {
    return (
      <MasterFrame active="day" stripe={data.availability.stripe} onStripeAction={data.reload}>
        <MasterBackBar to="/my/day" label="Мой день" />
        <EmptyState
          className={styles.missing}
          title="Записи нет в этом дне"
          text="Её могли перенести или отменить, пока экран был открыт. Откройте день заново — актуальное расписание там."
          action={{ label: "К моему дню", onClick: backToDay }}
        />
      </MasterFrame>
    )
  }

  const zone = data.timeZone
  const startMinute = minutesFromDayStart(appointment.startAt, zone, data.date)
  const endMinute = minutesFromDayStart(appointment.endAt, zone, data.date)
  const visual = statusVisual(appointment.status)
  const client = appointment.client
  const canEdit = data.availability.canEdit
  const deniedReason = canEdit ? undefined : data.availability.deniedReason ?? undefined
  const money = data.visibility.money
  const remainder = remainderMinor(appointment.priceMinor, appointment.prepaidMinor)
  const noShowCount = client.noShowCount

  // Каким каналом клиент записался сам. Мастер по этой строке решает, как напомнить
  // о себе: в Telegram он ответит в том же боте, а пришедшему со страницы записи
  // придётся звонить. Пусто — значит записал салон, и канала клиента нет вовсе.
  const channel = selfBookedChannel(appointment.source)

  // Полный номер бывает из двух источников: он пришёл вместе с днём (право
  // clients.phone.view) либо открыт запросом — сам при открытии карточки или нажатием
  // «Показать». Каждое открытие запросом пишется в журнал действий салона.
  const phoneAccess = phoneAccessOf(appointment)
  const fullPhone = client.phone ?? reveal.phone
  const revealPhone = (): void => {
    reveal.request(client.id, appointment.id)
  }

  return (
    <MasterFrame active="day" stripe={data.availability.stripe} onStripeAction={data.reload}>
      <MasterBackBar
        to="/my/day"
        label="Мой день"
        right={
          <Badge tone={appointment.status === "no_show" ? "error" : "ok"}>
            {visual.label === "" ? visual.name : visual.label}
          </Badge>
        }
      />

      <div className={styles.body}>
        <div>
          <p className={styles.time}>{formatRange(startMinute, endMinute)}</p>
          <p className={styles.sub}>
            {formatDateLong(data.date, zone)} · {data.branch?.name ?? ""}
          </p>
        </div>

        <div>
          <p className={styles.client}>{client.displayName}</p>
          <p className={styles.sub}>
            {visitsLabel(client.visitsCount)} к вам · {noShowLabel(noShowCount)}
            {channel === null ? "" : " · " + channel}
          </p>
        </div>

        {client.pinnedNotes.map((note) => (
          <p key={note} className={styles.note}>
            ! {note}
          </p>
        ))}

        {/* ТЕЛЕФОН. Номер открыт: номер и звонок в одно касание. Номер открывается по
            нажатию (умолчание салона): маска, объяснение, что открытие останется в журнале,
            и живая кнопка «Показать». Номер открывается сам (салон включил показ): пока
            идёт запрос — та же маска с «Открываем…», при отказе сети — «Показать» для
            повтора. Номер не открывается этому человеку — блока НЕТ вовсе: выключенное
            состояние — это отсутствие блока, а не серая строка (§5.2). */}
        {fullPhone !== null ? (
          <div className={styles.phoneRow}>
            <span className={styles.phone}>{formatPhone(fullPhone)}</span>
            <a className={styles.callLink} href={`tel:${fullPhone}`}>
              Позвонить
            </a>
          </div>
        ) : client.phoneMasked === null || phoneAccess === "none" || phoneAccess === "shown" ? null : (
          <MaskedField
            label="Телефон клиента"
            masked={client.phoneMasked}
            revealLabel="Показать"
            spoken="номер скрыт настройкой салона"
            hint="Открытие останется в журнале салона"
            revealing={reveal.running}
            onReveal={revealPhone}
          />
        )}

        {reveal.problem === null ? null : (
          <p className={styles.problem} role="alert">
            {reveal.problem.detail ?? reveal.problem.title}
          </p>
        )}

        <section>
          <h2 className={styles.caption}>Услуги</h2>
          <div className={styles.services}>
            {appointment.services.map((service, index) => (
              <div key={`${service.serviceId}-${String(index)}`} className={styles.serviceRow}>
                <div>
                  <p className={styles.serviceName}>{service.name}</p>
                  <p className={styles.serviceDuration}>{service.durationMinutes} мин</p>
                </div>
                {money && service.priceMinor !== null ? (
                  <span className={styles.servicePrice}>{formatMoney(service.priceMinor)}</span>
                ) : null}
              </div>
            ))}

            {money && appointment.priceMinor !== null ? (
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>
                  Итого
                  {appointment.prepaidMinor !== null && appointment.prepaidMinor > 0
                    ? ` · предоплата ${formatMoney(appointment.prepaidMinor)}`
                    : ""}
                  {remainder !== null && appointment.prepaidMinor !== null && appointment.prepaidMinor > 0
                    ? ` · остаток ${formatMoney(remainder)}`
                    : ""}
                </span>
                <span className={styles.totalValue}>{formatMoney(appointment.priceMinor)}</span>
              </div>
            ) : null}
          </div>
        </section>

        {appointment.comment === null || appointment.comment.trim() === "" ? null : (
          <section>
            <h2 className={styles.caption}>Комментарий клиента</h2>
            <p className={styles.comment}>{appointment.comment}</p>
          </section>
        )}

        <div className={styles.secondaryRow}>
          <Button
            className={styles.secondaryButton}
            deniedReason={deniedReason ?? "Перенос своей записи появится вместе с экраном 5-М5"}
          >
            Перенести
          </Button>
          <Button
            className={styles.secondaryButton}
            deniedReason={deniedReason ?? "Заметка о клиенте появится вместе с экраном 5-М7"}
          >
            Заметка
          </Button>
        </div>

        <p className={styles.explain}>
          Отменить запись может администратор: клиенту уходит уведомление, и решение
          принимает салон.
        </p>

        {actions.problem === null ? null : (
          <p className={styles.problem} role="alert">
            {actions.problem.code === "version_conflict"
              ? `Эту запись уже изменили${actions.problem.changedBy === null ? "" : `: ${actions.problem.changedBy}`}. Откройте день заново — действие применяется только к той версии, которую вы видели.`
              : (actions.problem.detail ?? actions.problem.title)}
          </p>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerRow}>
          <Button
            variant="primary"
            className={styles.footerButton}
            loading={actions.running === "arrived"}
            loadingLabel="Отмечаем…"
            deniedReason={deniedReason}
            onClick={() => {
              actions.arrive(appointment)
            }}
          >
            Клиент пришёл
          </Button>
          <Button
            className={styles.footerButton}
            loading={actions.running === "completed"}
            loadingLabel="Отмечаем…"
            deniedReason={deniedReason}
            onClick={() => {
              actions.complete(appointment)
            }}
          >
            Завершено
          </Button>
        </div>

        {/* «Не пришёл» стоит ОТДЕЛЬНОЙ строкой и не первым в ряду. Второе нажатие —
            не бюрократия: отметка тянет счётчик клиента и предоплату, и промах пальцем
            по краю экрана не должен ставить человеку неявку. */}
        {noShowOpen ? (
          <div className={styles.noShowBox}>
            <label className={styles.noShowLabel} htmlFor="master-no-show-comment">
              Что произошло — прочитает следующий администратор
            </label>
            <textarea
              id="master-no-show-comment"
              className={styles.noShowInput}
              value={noShowComment}
              placeholder="Необязательно"
              onChange={(event) => {
                setNoShowComment(event.currentTarget.value)
              }}
            />
            <p className={styles.noShowHint}>
              Ошиблись? Отметку можно снять в течение суток — счётчик пересчитается.
            </p>
            <div className={styles.noShowActions}>
              <Button
                className={styles.footerButton}
                onClick={() => {
                  setNoShowOpen(false)
                }}
              >
                Назад
              </Button>
              <Button
                variant="danger"
                className={styles.footerButton}
                loading={actions.running === "no-show"}
                loadingLabel="Отмечаем…"
                deniedReason={deniedReason}
                onClick={() => {
                  actions.noShow(appointment, noShowComment)
                }}
              >
                Не пришёл
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="danger-ghost"
            block
            className={styles.noShowButton}
            deniedReason={deniedReason}
            onClick={() => {
              setNoShowOpen(true)
            }}
          >
            Не пришёл…
          </Button>
        )}
      </div>
    </MasterFrame>
  )
}
